import React, { useMemo } from 'react';
import 'styles/ant-design.css';
import ExcelTable from 'components/ui/excel-table';
import FormSection from 'components/ui/form-section';
import { useTranslation } from 'react-i18next';
import {
  ECellValueType,
  ExcelTableColDef,
} from 'components/ui/excel-table/types';
import dayjs from 'dayjs';
import { EQueryKeys } from 'constants/query-keys';
import { useQuery } from 'react-query';
import { premiumScheduleApi } from 'services/apis';
import { formatReceiptNumber } from 'utils/format';
import { useNavigate } from 'react-router';
import { ENavigationPath } from 'constants/path';
import { extractReceiptIdFromReceiptNo } from 'utils';

interface PremiumPaidTableProps {
  currency?: string;
  contractId?: string;
}

const PremiumPaidTable: React.FC<PremiumPaidTableProps> = ({
  contractId,
  currency,
}) => {
  const { data: premiumPaidData, isLoading: isLoadingPremiumPaid } = useQuery(
    [EQueryKeys.CONTRACT_PREMIUM_PAID, contractId, currency],
    async () => {
      if (!contractId) {
        return undefined;
      }
      return premiumScheduleApi.getAllocatedPremiumScheduleByContractId({
        contractId,
        currency,
      });
    },
    {
      enabled: !!contractId,
    }
  );

  const { t } = useTranslation();

  const navigate = useNavigate();

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        headerName: t('premiumProcessing.documentNo'),
        field: 'pinnacleRefNo',
        flex: 1,
        minWidth: 150,
        editable: false,
        resizable: true,
      },
      {
        headerName: 'Receipt No',
        field: 'receiptNo',
        flex: 1,
        minWidth: 150,
        editable: false,
        resizable: true,
        onCellClicked: (params) => {
          const receiptId = extractReceiptIdFromReceiptNo(params.value);
          navigate(ENavigationPath.UPDATE_RECEIPT.replace(':id', receiptId));
        },
      },
      {
        headerName: 'Payment Date',
        field: 'paymentDate',
        flex: 1,
        minWidth: 150,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.ppwDueDate'),
        field: 'ppwDueDate',
        flex: 1,
        minWidth: 150,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.shareOfPremiumSigned'),
        field: 'shareOfPremiumSigned',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        flex: 1,
        minWidth: 180,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.gstOnPremium'),
        field: 'gstOnPremium',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        minWidth: 150,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.cedingCommission'),
        field: 'cedingCommission',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        flex: 1,
        minWidth: 150,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.gstOnCedingCommission'),
        field: 'gstOnCedingCommission',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        flex: 1,
        minWidth: 180,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.sourceBrokerage'),
        field: 'sourceBrokerage',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        flex: 1,
        minWidth: 150,
        resizable: true,
        editable: false,
      },
      {
        headerName: 'GST on Source Brokerage',
        field: 'gstOnSourceBrokerage',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        flex: 1,
        minWidth: 180,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.ere'),
        field: 'ere',
        minWidth: 120,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        resizable: true,
        editable: false,
        flex: 1,
      },
      {
        headerName: t('premiumProcessing.withholdingTax'),
        field: 'withholdingTax',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        minWidth: 150,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.netPremiumDueFromBroker'),
        field: 'netPremiumDueFromBroker',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.pinnacleCommission'),
        field: 'pinnacleCommission',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        minWidth: 170,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.ait'),
        field: 'ait',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        minWidth: 120,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.taxesAndFees'),
        field: 'taxesAndFees',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        minWidth: 150,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.binderCashRemittance'),
        field: 'binderCashRemittance',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        minWidth: 180,
        resizable: true,
        editable: false,
      },
    ],
    [t]
  );

  const data = useMemo(() => {
    return (premiumPaidData || []).map((item) => ({
      ...item,
      paymentDate: dayjs(item.paymentDate).format('DD/MM/YYYY'),
      ppwDueDate: dayjs(item.ppwDueDate).format('DD/MM/YYYY'),
      receiptNo: formatReceiptNumber(Number(item.receiptNo)),
    }));
  }, [premiumPaidData]);

  return (
    <>
      <FormSection title={t('premiumProcessing.premiumPaid')} collapsible>
        <ExcelTable
          data={data}
          columnDefs={columnDefs}
          includeIdRow={false}
          tableContainerClassName="h-[24rem]"
          loading={isLoadingPremiumPaid}
        />
      </FormSection>
    </>
  );
};

export default PremiumPaidTable;
